import Paths from "./Paths";
import OtpData from "@/app/model/OtpData";
import { getApiKey } from "@/app/utils/EnvConfig";
import { ITalkie, Talkie } from "@dev/hc-talk-talk";
import AuthorizationKeyRequest from "@/app/dto/AuthorizationKeyRequest";
import AuthorizeConsentRequest from "@/app/dto/AuthorizeConsentRequest";
import CheckConsentRequest from "@/app/dto/CheckConsentRequest";
import CheckCredentialsRequest from "@/app/dto/CheckCredentialsRequest";
import ContractWithdrawalIdentifyRequest from "@/app/dto/ContractWithdrawalIdentifyRequest";
import ContractWithdrawalTerminateRequest from "@/app/dto/ContractWithdrawalTerminateRequest";
import IAllowedFactorsResponse from "@/app/dto/IAllowedFactorsResponse";
import ICheckCredentialsResponse from "@/app/dto/ICheckCredentialsResponse";
import IConsentResponse from "@/app/dto/IConsentResponse";
import IContractWithdrawalIdentifyResponse from "@/app/dto/IContractWithdrawalIdentifyResponse";
import ICreateKeyResponse from "@/app/dto/ICreateKeyResponse";
import IInitPasswordRenewalResponse from "@/app/dto/IInitPasswordRenewalResponse";
import ILoginResponse from "@/app/dto/ILoginResponse";
import InitPasswordRenewalRequest from "@/app/dto/InitPasswordRenewalRequest";
import IOtpResponse from "@/app/dto/IOtpResponse";
import LoginRequest from "@/app/dto/LoginRequest";
import OtpRequest from "@/app/dto/OtpRequest";
import ProcessEnum from "@/app/dto/ProcessEnum";
import SetPasswordRequest from "@/app/dto/SetPasswordRequest";
import BasePath from "@/app/service/api/BasePath";

class Api {
    private readonly apiClient: ITalkie;

    constructor() {
        this.apiClient = new Talkie();
    }

    public async getAllowedFactorsExternal(customerUuid: string, process: ProcessEnum): Promise<IAllowedFactorsResponse | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(process),
            this.getAuthorizationHeaders(customerUuid)
        );

        return this.apiClient.get<IAllowedFactorsResponse>(Paths.getAvailableFactorsExternal, undefined, headers);
    }

    public async authorizationKeyExternal(
        customerUuid: string,
        request: AuthorizationKeyRequest,
        process: ProcessEnum
    ): Promise<ICreateKeyResponse | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(process),
            this.getAuthorizationHeaders(customerUuid)
        );

        return await this.apiClient.post<AuthorizationKeyRequest, ICreateKeyResponse>(
            Paths.authorizationsKeysExternal,
            request,
            headers
        );
    }

    public async sendOtpExternal(customerUuid: string, authorizationKey: string, process: ProcessEnum): Promise<IOtpResponse | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(process),
            this.getAuthorizationHeaders(customerUuid)
        );

        return await this.apiClient.post<object, IOtpResponse>(new BasePath(`client/v5/external/authorizations/keys/${authorizationKey}/otp/send`), {}, headers);
    }

    public async getAllowedFactorsProtected(token: string, process: ProcessEnum): Promise<IAllowedFactorsResponse | null> {
        const headers = Object.assign(
            this.getToken(token),
            this.getConsumerContextHeaders(process)
        );

        return this.apiClient.get<IAllowedFactorsResponse>(Paths.getAvailableFactorsProtected, undefined, headers);
    }

    public async authorizationKeyProtected(
        token: string,
        request: AuthorizationKeyRequest,
        process: ProcessEnum
    ): Promise<ICreateKeyResponse | null> {
        const headers = Object.assign(
            this.getToken(token),
            this.getConsumerContextHeaders(process)
        );

        return await this.apiClient.post<AuthorizationKeyRequest, ICreateKeyResponse>(
            Paths.authorizationsKeysProtected,
            request,
            headers
        );
    }

    public async sendOtpProtected(token: string, authorizationKey: string, request: OtpRequest, process: ProcessEnum): Promise<IOtpResponse | null> {
        const headers = Object.assign(
            this.getToken(token),
            this.getConsumerContextHeaders(process)
        );

        return await this.apiClient.post<object, IOtpResponse>(
            new BasePath(`client/v5/protected/authorizations/keys/${authorizationKey}/otp/send`),
            request,
            headers
        );
    }

    public async initPasswordRenewal(request: InitPasswordRenewalRequest): Promise<IInitPasswordRenewalResponse | null> {
        const headers = Object.assign(this.getApiKeyHeaders(), this.getConsumerContextHeaders(ProcessEnum.PASSWORD_RENEWAL));
        return await this.apiClient.post<InitPasswordRenewalRequest, IInitPasswordRenewalResponse>(
            Paths.initPasswordRenewal,
            request,
            headers
        );
    }

    public async setPassword(
        customerUuid: string,
        authKey: string,
        otp: OtpData,
        request: SetPasswordRequest
    ): Promise<void | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(ProcessEnum.PASSWORD_RENEWAL),
            this.getAuthorizationHeaders(customerUuid, authKey),
            this.getAuthorizationDataHeaders(otp)
        );

        return await this.apiClient.post<SetPasswordRequest, void>(
            Paths.setPassword,
            request,
            headers
        );
    }

    public async checkCredentials(request: CheckCredentialsRequest): Promise<ICheckCredentialsResponse | null> {
        const headers = Object.assign(this.getApiKeyHeaders(), this.getConsumerContextHeaders(ProcessEnum.LOGIN));
        return await this.apiClient.post<CheckCredentialsRequest, ICheckCredentialsResponse>(
            Paths.checkCredentials,
            request,
            headers
        );
    }

    public async login(
        customerUuid: string,
        authKey: string,
        otp: OtpData,
        password: string,
        request: LoginRequest
    ): Promise<ILoginResponse | null> {
        const headers = Object.assign(
            this.getApiKeyHeaders(),
            this.getConsumerContextHeaders(ProcessEnum.LOGIN),
            this.getAuthorizationHeaders(customerUuid, authKey),
            this.getAuthorizationDataHeaders(otp, password)
        );

        return await this.apiClient.post<LoginRequest, ILoginResponse>(
            Paths.login,
            request,
            headers
        );
    }

    public async checkConsent(request: CheckConsentRequest): Promise<IConsentResponse | null> {
        const headers = Object.assign(this.getApiKeyHeaders());
        return await this.apiClient.post<CheckConsentRequest, IConsentResponse>(
            Paths.checkConsent,
            request,
            headers
        );
    }

    public async authorizeConsent(token: string, request: AuthorizeConsentRequest): Promise<IConsentResponse | null> {
        const headers = Object.assign(this.getToken(token));
        return await this.apiClient.post<CheckConsentRequest, IConsentResponse>(
            Paths.authorizeConsent,
            request,
            headers
        );
    }


    public async contractWithdrawalIdentify(request: ContractWithdrawalIdentifyRequest): Promise<IContractWithdrawalIdentifyResponse | null> {
        const headers = Object.assign(this.getApiKeyHeaders(), this.getConsumerContextHeaders());
        return await this.apiClient.post<ContractWithdrawalIdentifyRequest, IContractWithdrawalIdentifyResponse>(
            Paths.contractWithdrawalIdentify,
            request,
            headers
        );
    }

    public async contractWithdrawalTerminate(token: string, authKey: string, otp: OtpData, request: ContractWithdrawalTerminateRequest): Promise<void | null> {
        const headers = Object.assign(
            this.getToken(token),
            this.getConsumerContextHeaders(ProcessEnum.CONTRACT_WITHDRAWAL),
            this.getAuthorizationHeaders(undefined, authKey),
            this.getAuthorizationDataHeaders(otp)
        );
        return await this.apiClient.post<ContractWithdrawalTerminateRequest, void>(
            Paths.contractWithdrawalTerminate,
            request,
            headers
        );
    }

    private getConsumerContextHeaders(process?: ProcessEnum): { [fieldName: string]: string } {
        let processParameterString: string = !!process ? `|process:${process}` : "";

        return {
            "X-Consumer-Context": `channel:WEB_LOGIN${processParameterString}`
        };
    }

    private getApiKeyHeaders(): { [fieldName: string]: string } {
        return {
            apiKey: getApiKey()
        };
    }

    private getToken(token: string): { [fieldName: string]: string } {
        return {
            Authorization: `Bearer ${token}`
        };
    }

    private getAuthorizationHeaders(resourceId?: string, key?: string): { [fieldName: string]: string } {
        let keyParameterString: string = !!key ? `|key:${key}` : "";
        let resourceParameterString: string = !!resourceId ? `|resource_id:${resourceId}|resource_type:CUSTOMER` : "";

        return {
            "X-Authorization": `auth_version:3.0${resourceParameterString}${keyParameterString}`
        };
    }

    private getAuthorizationDataHeaders(otp: OtpData, password?: string): { [fieldName: string]: string } {
        let passwordParameterString: string = !!password ? `|password_value:${password}` : "";

        return {
            "X-Authorization-Data": `otp_code:${otp.code}|otp_key:${otp.key}${passwordParameterString}`
        };
    }
}

export default new Api();
